import { imageSizes, imageSrcSet, imageUrl } from "@components/componentUtils"
import { NewsDetailPage } from "@components/pages/NewsDetailPage"
import { SiteShell } from "@components/SiteShell"
import { newsDetailPageQueryOptions } from "@data/publicPageDataQuery"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { isSupportedLocale } from "@utils/locale"
import { localizedRouteHead } from "@utils/routeHead"
import { pageLabels } from "@utils/routeLabels"

export const Route = createFileRoute("/$locale/news/$slug")({
  beforeLoad: ({ params }) => {
    if (!isSupportedLocale(params.locale)) throw redirect({ to: "/404" })
  },
  loader: async ({ context, params }) => {
    const locale = params.locale as "en" | "ja"
    const data = await context.queryClient.ensureQueryData(newsDetailPageQueryOptions(locale, params.slug))
    if (!data) throw redirect({ href: `/${locale}/news` })
    return data
  },
  head: ({ params, loaderData }) => {
    const locale = params.locale as "en" | "ja"
    const post = loaderData?.post
    const head = localizedRouteHead({
      locale,
      page: "news",
      path: `/${locale}/news/${params.slug}`,
      title: post?.title ? `${post.title} | PLAYBRAIN Inc.` : undefined,
      description: post?.metaOgDescription || post?.metaDescription || post?.newsCoverBlurb,
      image: post?.newsCoverImage ? (imageUrl(post.newsCoverImage, "newsDetail") ?? undefined) : undefined,
      type: "article",
    })
    if (post?.newsCoverImage) {
      head.links.push({
        rel: "preload",
        as: "image",
        href: imageUrl(post.newsCoverImage, "newsDetail") ?? "",
        imageSrcSet: imageSrcSet(post.newsCoverImage, "newsDetail"),
        imageSizes: imageSizes("newsDetail"),
        fetchPriority: "high",
      })
    }
    return head
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { locale: routeLocale } = Route.useParams()
  const locale = routeLocale as "en" | "ja"
  const { post } = Route.useLoaderData()
  const labels = pageLabels(locale, "news")
  return (
    <SiteShell
      locale={locale}
      page={labels.page}
    >
      <NewsDetailPage
        locale={locale}
        post={post}
      />
    </SiteShell>
  )
}
